import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { captureError, initSentry } from '@aosenuma/lib-supabase';
import { ErrorBoundary } from '@aosenuma/ui';
import { App } from './App';

import '@aosenuma/ui/styles/tokens.css';
import '@aosenuma/ui/styles/globals.css';
import '@aosenuma/ui/styles/pagination.css';
import '@aosenuma/ui/styles/error-boundary.css';
import '@aosenuma/sig-viewer/styles/toolbar.css';
import './styles/equipo.css';

// Sentry: técnicos suben evidencias con PII de propietarios; errores aquí
// pueden costar evidencia. No-op si VITE_PUBLIC_SENTRY_DSN falta.
void initSentry({ app: 'equipo' });

// Service Worker (vite-plugin-pwa): auto-update sin vaciar caché.
//
// La PWA (registerType:'autoUpdate') hace que el SW nuevo se active solo
// (skipWaiting+clientsClaim). Aquí (a) forzamos chequeo de updates periódico
// + al recuperar foco/conexión, y (b) recargamos UNA vez cuando el SW nuevo
// toma control — PERO con un guard que NUNCA recarga durante el flujo de auth.
//
// IMPORTANTE (bug histórico): recargar durante el AuthCallback (procesando
// ?code / #access_token → setSession → redirect) rompe el login con pantalla
// en negro. Por eso `safeToReload()` excluye /auth/* y URLs con esos params;
// en ese caso diferimos: el código nuevo entra en la próxima navegación.
if ('serviceWorker' in navigator) {
  const hadController = !!navigator.serviceWorker.controller;
  let reloaded = false;
  const safeToReload = () => {
    const { pathname, search, hash } = window.location;
    if (pathname.startsWith('/auth')) return false;
    if (/[?&]code=/.test(search)) return false;
    if (/access_token|refresh_token|type=(recovery|magiclink|signup|invite)/.test(hash)) return false;
    return true;
  };
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded || !hadController || !safeToReload()) return;
    reloaded = true;
    window.location.reload();
  });
  void import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({
      immediate: true,
      onRegisteredSW(_swUrl, reg) {
        if (!reg) return;
        const check = () => { void reg.update().catch(() => undefined); };
        window.setInterval(check, 30 * 60 * 1000); // cada 30 min
        window.addEventListener('focus', check);
        window.addEventListener('online', check);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') check();
        });
      },
    });
  }).catch(() => { /* dev mode sin SW build, no es error */ });
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary
      app="equipo"
      onError={(err, info) => {
        void captureError(err, { componentStack: info.componentStack ?? null, app: 'equipo' });
      }}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
